import { User, WebStorageStateStore } from 'oidc-client-ts';
import { ProductConstants } from '../../constants/ProductConstants';
import { AppStore } from '../../redux/app/store';

function removeOidcParameters(url: URL) {
    url.searchParams.delete('code');
    url.searchParams.delete('scope');
    url.searchParams.delete('session_state');
    url.searchParams.delete('iss');
    url.searchParams.delete('state');
    url.searchParams.delete('loggedout');
    return url;
}

interface OidcProviderConfig {
    metadataUrl: string;
    authority: string;
    clientId: string;
    redirectUri: string;
    apiScope: string;
    providerName: string;
    extraQueryParams?: Record<string, string | number | boolean>;
}

const providers: Record<string, OidcProviderConfig> = {
    // Default provider
    default: {
        metadataUrl: process.env.REACT_APP_OPENIDCONNECT_METADATA_URL ?? '',
        authority: process.env.REACT_APP_OPENIDCONNECT_AUTHORITY ?? '',
        clientId: process.env.REACT_APP_OPENIDCONNECT_CLIENT_ID ?? '',
        redirectUri: process.env.REACT_APP_OPENIDCONNECT_REDIRECT_URI ?? '',
        apiScope: `openid profile email roles ${process.env.REACT_APP_OPENIDCONNECT_API_SCOPE ?? ''} offline_access`,
        providerName: 'vismaonline',
    },
    // Specific provider for speedledger.com
    speedledger: {
        metadataUrl: process.env.REACT_APP_SPEEDLEDGER_OPENIDCONNECT_METADATA_URL ?? '',
        authority: process.env.REACT_APP_SPEEDLEDGER_OPENIDCONNECT_AUTHORITY ?? '',
        clientId: process.env.REACT_APP_SPEEDLEDGER_OPENIDCONNECT_CLIENT_ID ?? '',
        redirectUri: process.env.REACT_APP_SPEEDLEDGER_OPENIDCONNECT_REDIRECT_URI ?? '',
        apiScope: `openid ${process.env.REACT_APP_SPEEDLEDGER_OPENIDCONNECT_API_SCOPE ?? ''}`,
        providerName: 'speedledger',
        extraQueryParams: {
            audience: 'AIAssistant',
        },
    },
};

const PROVIDER_KEY_EMBEDDED = 'em_provider';
const PROVIDER_KEY_STANDALONE = 'sa_provider';

const isEmbedded = () => window.self !== window.top;

const persistProviderId = (providerId: string) => {
    const key = isEmbedded() ? PROVIDER_KEY_EMBEDDED : PROVIDER_KEY_STANDALONE;
    if (providerId in providers) {
        localStorage.setItem(key, providerId);
    } else {
        localStorage.setItem(key, 'default');
    }
};
const getProviderId = (): string | null => {
    const key = isEmbedded() ? PROVIDER_KEY_EMBEDDED : PROVIDER_KEY_STANDALONE;
    return localStorage.getItem(key);
};

const getProviderConfig = (store: AppStore): OidcProviderConfig => {
    const providerId = getProviderId() ?? store.getState().hostContext.hostContext.id;
    const matchingProvider = Object.keys(providers).find((provider) => provider === providerId);
    return providers[matchingProvider ?? 'default'];
};

// Only these hosts can get the Spiris MCP (webapi SpirisAvailability, which also checks the scope, SE and the
// eAccounting product), so its scope is not requested elsewhere.
const spirisMcpHostIds = [ProductConstants.EAccounting.ProductName.toLowerCase(), 'standalone'];

const buildScope = (config: OidcProviderConfig, hostId: string | undefined): string => {
    const spirisMcpScope = process.env.REACT_APP_SPIRIS_MCP_SCOPE?.trim();
    const spirisMcpHost = spirisMcpHostIds.includes((hostId ?? '').toLowerCase());
    return spirisMcpScope && spirisMcpHost && config.providerName === 'vismaonline'
        ? `${config.apiScope} ${spirisMcpScope}`
        : config.apiScope;
};

const getOidcConfig = (store: AppStore) => {
    const config = getProviderConfig(store);

    return {
        accessTokenExpiringNotificationTimeInSeconds: 240,
        metadataUrl: config.metadataUrl,
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        post_logout_redirect_uri: config.redirectUri,
        response_type: 'code',
        scope: buildScope(config, store.getState().hostContext.hostContext.id),
        userStore: new WebStorageStateStore({ store: window.localStorage }),
        extraQueryParams: config.extraQueryParams,
        onSigninCallback: (_user: User | undefined): void => {
            const url = new URL(window.location.href);
            window.history.replaceState({}, document.title, removeOidcParameters(url).toString());
        },
    };
};

export const OidcAuthHelper = {
    buildScope,
    getOidcConfig,
    getProviderConfig,
    persistProviderId,
};
